import { createAction, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  Workspace,
  WorkspaceIntegration,
  WorkspaceIntegrationMetadataItem,
  WorkspaceTeamMember,
  WorkspaceTeamPage,
} from '@/features/workspace/types';
import {
  type FetchUserWorkspacesResult,
  createSocialMediaLink,
  createWorkspaceThunk,
  deleteSocialMediaLink,
  fetchUserWorkspaces,
  fetchWorkspaceById,
  fetchWorkspaceDetails,
  fetchWorkspaceIntegrations,
  fetchWorkspaceIntegrationMetadata,
  fetchWorkspaceTeam,
  deleteWorkspaceIntegration,
  setupWorkspaceIntegration,
  inviteWorkspaceTeamMember,
  removeWorkspaceTeamMember,
  transferWorkspaceOwnership,
  updateVisualIdentity,
  cancelWorkspaceSubscription,
  updateWorkspace,
  updateWorkspaceTeamMemberRole,
  uploadLogo,
} from './workspaceThunks';
import { logout } from '@/features/auth/authThunks';
import {
  CURRENT_WORKSPACE_ID_STORAGE_KEY,
  getStoredWorkspaceId,
} from '@/lib/storedWorkspaceRestore';
import { hasFreeRegistrationLimit } from '@/lib/partnerOfferPlan';

export { CURRENT_WORKSPACE_ID_STORAGE_KEY, getStoredWorkspaceId };

export interface WorkspaceState {
  currentWorkspace: Workspace | null;
  workspacesList: Workspace[] | null;
  /** Dérivé du dernier GET `/workspaces` paginé (avec les workspaces enrichis). */
  workspacesTotalPages: number | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  socialMediaLoading: boolean;
  socialMediaError: string | null;
  team: WorkspaceTeamMember[];
  teamMeta: {
    number: number;
    size: number;
    totalElements: number;
    totalPages: number;
  } | null;
  teamLoading: boolean;
  teamError: string | null;
  teamActionLoading: boolean;
  teamActionError: string | null;
  integrations: WorkspaceIntegration[];
  integrationsLoading: boolean;
  integrationsError: string | null;
  integrationsActionLoading: boolean;
  integrationsActionError: string | null;
  integrationMetadata: WorkspaceIntegrationMetadataItem[];
  integrationMetadataLoading: boolean;
  integrationMetadataError: string | null;
  integrationMetadataNotConnected: boolean;
  subscriptionCancelLoading: boolean;
  subscriptionCancelError: string | null;
}

const initialState: WorkspaceState = {
  currentWorkspace: null,
  workspacesList: null,
  workspacesTotalPages: null,
  loading: false,
  error: null,
  createLoading: false,
  socialMediaLoading: false,
  socialMediaError: null,
  team: [],
  teamMeta: null,
  teamLoading: false,
  teamError: null,
  teamActionLoading: false,
  teamActionError: null,
  integrations: [],
  integrationsLoading: false,
  integrationsError: null,
  integrationsActionLoading: false,
  integrationsActionError: null,
  integrationMetadata: [],
  integrationMetadataLoading: false,
  integrationMetadataError: null,
  integrationMetadataNotConnected: false,
  subscriptionCancelLoading: false,
  subscriptionCancelError: null,
};

const updateTeamState = (state: WorkspaceState, page?: WorkspaceTeamPage | null) => {
  if (!page) {
    state.team = [];
    state.teamMeta = null;
    return;
  }
  state.team = page.content ?? [];
  state.teamMeta = {
    number: page.number,
    size: page.size,
    totalElements: page.totalElements,
    totalPages: page.totalPages,
  };
};

export const switchWorkspace = createAction<Workspace>('workspace/switch');

const slice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    setCurrentWorkspace: (state, action: PayloadAction<Workspace | null>) => {
      if (action.payload) {
        state.currentWorkspace = {
          ...action.payload,
          socials: action.payload.socials ?? [],
        };
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(CURRENT_WORKSPACE_ID_STORAGE_KEY, String(action.payload.id));
          } catch {}
        }
      } else {
        state.currentWorkspace = null;
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem(CURRENT_WORKSPACE_ID_STORAGE_KEY);
          } catch {}
        }
      }
    },
    clearCurrentWorkspace: (state) => {
      state.currentWorkspace = null;
    },
    resetWorkspaceData: (state) => {
      state.loading = initialState.loading;
      state.error = initialState.error;
      state.createLoading = initialState.createLoading;
      state.socialMediaLoading = initialState.socialMediaLoading;
      state.socialMediaError = initialState.socialMediaError;
      state.team = initialState.team;
      state.teamMeta = initialState.teamMeta;
      state.teamLoading = initialState.teamLoading;
      state.teamError = initialState.teamError;
      state.teamActionLoading = initialState.teamActionLoading;
      state.teamActionError = initialState.teamActionError;
      state.integrations = initialState.integrations;
      state.integrationsLoading = initialState.integrationsLoading;
      state.integrationsError = initialState.integrationsError;
      state.integrationsActionLoading = initialState.integrationsActionLoading;
      state.integrationsActionError = initialState.integrationsActionError;
      state.integrationMetadata = initialState.integrationMetadata;
      state.integrationMetadataLoading = initialState.integrationMetadataLoading;
      state.integrationMetadataError = initialState.integrationMetadataError;
      state.integrationMetadataNotConnected = initialState.integrationMetadataNotConnected;
      state.subscriptionCancelLoading = initialState.subscriptionCancelLoading;
      state.subscriptionCancelError = initialState.subscriptionCancelError;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserWorkspaces.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchUserWorkspaces.fulfilled,
      (state, action: PayloadAction<FetchUserWorkspacesResult>) => {
        state.loading = false;
        const { content, totalPages } = action.payload;
        state.workspacesList = content;
        state.workspacesTotalPages = totalPages;

        if (content.length === 0) {
          if (totalPages <= 1) {
            state.currentWorkspace = null;
          }
          return;
        }

        if (state.currentWorkspace != null) {
          const currentId = state.currentWorkspace.id;
          const refreshed = content.find((w) => w.id === currentId);
          if (refreshed) {
            state.currentWorkspace = {
              ...refreshed,
              socials: refreshed.socials ?? [],
            };
          }
          return;
        }

        const storedId = getStoredWorkspaceId();
        const chosen = storedId != null ? content.find((w) => w.id === storedId) : null;
        const workspace = chosen ?? action.payload.restored ?? content[0];
        state.currentWorkspace = {
          ...workspace,
          socials: workspace.socials ?? [],
        };
      },
    );
    builder.addCase(fetchUserWorkspaces.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error = payload?.message ?? action.error.message ?? 'Failed to fetch workspaces';
    });

    builder.addCase(createWorkspaceThunk.pending, (state) => {
      state.createLoading = true;
      state.error = null;
    });
    builder.addCase(createWorkspaceThunk.fulfilled, (state) => {
      state.createLoading = false;
      state.error = null;
    });
    builder.addCase(createWorkspaceThunk.rejected, (state, action) => {
      state.createLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error = payload?.message ?? action.error.message ?? 'Failed to create workspace';
    });

    builder.addCase(createSocialMediaLink.pending, (state) => {
      state.socialMediaLoading = true;
      state.socialMediaError = null;
    });
    builder.addCase(createSocialMediaLink.fulfilled, (state, action) => {
      state.socialMediaLoading = false;
      state.socialMediaError = null;
      if (state.currentWorkspace) {
        state.currentWorkspace.socials = action.payload ?? [];
      }
    });
    builder.addCase(createSocialMediaLink.rejected, (state, action) => {
      state.socialMediaLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.socialMediaError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(deleteSocialMediaLink.pending, (state) => {
      state.socialMediaLoading = true;
      state.socialMediaError = null;
    });
    builder.addCase(deleteSocialMediaLink.fulfilled, (state, action) => {
      state.socialMediaLoading = false;
      state.socialMediaError = null;
      if (state.currentWorkspace) {
        state.currentWorkspace.socials = action.payload ?? [];
      }
    });
    builder.addCase(deleteSocialMediaLink.rejected, (state, action) => {
      state.socialMediaLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.socialMediaError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(fetchWorkspaceIntegrations.pending, (state) => {
      state.integrationsLoading = true;
      state.integrationsError = null;
    });
    builder.addCase(
      fetchWorkspaceIntegrations.fulfilled,
      (state, action: PayloadAction<WorkspaceIntegration[]>) => {
        state.integrationsLoading = false;
        state.integrations = action.payload ?? [];
      },
    );
    builder.addCase(fetchWorkspaceIntegrations.rejected, (state, action) => {
      state.integrationsLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.integrationsError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(setupWorkspaceIntegration.pending, (state) => {
      state.integrationsActionLoading = true;
      state.integrationsActionError = null;
    });
    builder.addCase(
      setupWorkspaceIntegration.fulfilled,
      (state, action: PayloadAction<WorkspaceIntegration[]>) => {
        state.integrationsActionLoading = false;
        state.integrationsActionError = null;
        state.integrations = action.payload ?? [];
      },
    );
    builder.addCase(setupWorkspaceIntegration.rejected, (state, action) => {
      state.integrationsActionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.integrationsActionError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(deleteWorkspaceIntegration.pending, (state) => {
      state.integrationsActionLoading = true;
      state.integrationsActionError = null;
    });
    builder.addCase(
      deleteWorkspaceIntegration.fulfilled,
      (state, action: PayloadAction<WorkspaceIntegration[]>) => {
        state.integrationsActionLoading = false;
        state.integrationsActionError = null;
        state.integrations = action.payload ?? [];
      },
    );
    builder.addCase(deleteWorkspaceIntegration.rejected, (state, action) => {
      state.integrationsActionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.integrationsActionError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(fetchWorkspaceIntegrationMetadata.pending, (state) => {
      state.integrationMetadataLoading = true;
      state.integrationMetadataError = null;
      state.integrationMetadataNotConnected = false;
    });
    builder.addCase(
      fetchWorkspaceIntegrationMetadata.fulfilled,
      (state, action: PayloadAction<WorkspaceIntegrationMetadataItem[]>) => {
        state.integrationMetadataLoading = false;
        state.integrationMetadataNotConnected = false;
        state.integrationMetadata = action.payload ?? [];
      },
    );
    builder.addCase(fetchWorkspaceIntegrationMetadata.rejected, (state, action) => {
      state.integrationMetadataLoading = false;
      state.integrationMetadata = [];
      const payload = action.payload as
        | { message?: string; integrationNotConnected?: boolean }
        | undefined;
      state.integrationMetadataError = payload?.message ?? action.error.message ?? null;
      state.integrationMetadataNotConnected = Boolean(payload?.integrationNotConnected);
    });

    builder.addCase(fetchWorkspaceTeam.pending, (state) => {
      state.teamLoading = true;
      state.teamError = null;
    });
    builder.addCase(
      fetchWorkspaceTeam.fulfilled,
      (state, action: PayloadAction<WorkspaceTeamPage>) => {
        state.teamLoading = false;
        updateTeamState(state, action.payload);
      },
    );
    builder.addCase(fetchWorkspaceTeam.rejected, (state, action) => {
      state.teamLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.teamError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(inviteWorkspaceTeamMember.pending, (state) => {
      state.teamActionLoading = true;
      state.teamActionError = null;
    });
    builder.addCase(
      inviteWorkspaceTeamMember.fulfilled,
      (state, action: PayloadAction<WorkspaceTeamPage>) => {
        state.teamActionLoading = false;
        updateTeamState(state, action.payload);
      },
    );
    builder.addCase(inviteWorkspaceTeamMember.rejected, (state, action) => {
      state.teamActionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.teamActionError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(updateWorkspaceTeamMemberRole.pending, (state) => {
      state.teamActionLoading = true;
      state.teamActionError = null;
    });
    builder.addCase(
      updateWorkspaceTeamMemberRole.fulfilled,
      (state, action: PayloadAction<WorkspaceTeamPage>) => {
        state.teamActionLoading = false;
        updateTeamState(state, action.payload);
      },
    );
    builder.addCase(updateWorkspaceTeamMemberRole.rejected, (state, action) => {
      state.teamActionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.teamActionError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(removeWorkspaceTeamMember.pending, (state) => {
      state.teamActionLoading = true;
      state.teamActionError = null;
    });
    builder.addCase(
      removeWorkspaceTeamMember.fulfilled,
      (state, action: PayloadAction<WorkspaceTeamPage>) => {
        state.teamActionLoading = false;
        updateTeamState(state, action.payload);
      },
    );
    builder.addCase(removeWorkspaceTeamMember.rejected, (state, action) => {
      state.teamActionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.teamActionError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(transferWorkspaceOwnership.pending, (state) => {
      state.teamActionLoading = true;
      state.teamActionError = null;
    });
    builder.addCase(
      transferWorkspaceOwnership.fulfilled,
      (state, action: PayloadAction<WorkspaceTeamPage>) => {
        state.teamActionLoading = false;
        updateTeamState(state, action.payload);
      },
    );
    builder.addCase(transferWorkspaceOwnership.rejected, (state, action) => {
      state.teamActionLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.teamActionError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(fetchWorkspaceDetails.fulfilled, (state, action) => {
      if (state.currentWorkspace) {
        state.currentWorkspace.visualIdentity = action.payload.visualIdentity;
        state.currentWorkspace.socials = action.payload.socials;
      }
    });

    builder.addCase(updateVisualIdentity.fulfilled, (state, action) => {
      if (state.currentWorkspace && action.payload) {
        state.currentWorkspace.visualIdentity = action.payload;
      }
    });

    builder.addCase(uploadLogo.fulfilled, (state, action) => {
      if (state.currentWorkspace && action.payload) {
        state.currentWorkspace.visualIdentity = action.payload;
      }
    });

    builder.addCase(fetchWorkspaceById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchWorkspaceById.fulfilled, (state, action) => {
      state.loading = false;
      if (state.currentWorkspace && state.currentWorkspace.id === action.payload.id) {
        state.currentWorkspace.name = action.payload.name;
        state.currentWorkspace.timezone = action.payload.timezone;
        state.currentWorkspace.subscriptionStatus = action.payload.subscriptionStatus;
        state.currentWorkspace.billingCurrency = action.payload.billingCurrency;
        state.currentWorkspace.legacyTrialEndDate = action.payload.legacyTrialEndDate ?? null;
        state.currentWorkspace.promoCode = action.payload.promoCode ?? null;
        state.currentWorkspace.productAccessStatus = action.payload.productAccessStatus;
        state.currentWorkspace.productAccessPauseEndsAt =
          action.payload.productAccessPauseEndsAt ?? null;
        state.currentWorkspace.billingRegularizationBanner =
          action.payload.billingRegularizationBanner ?? null;
        state.currentWorkspace.camLayout = action.payload.camLayout ?? null;
      } else if (!state.currentWorkspace) {
        // Si currentWorkspace est null (page publique), on le définit quand même
        state.currentWorkspace = {
          id: action.payload.id,
          name: action.payload.name,
          timezone: action.payload.timezone,
          currentUserRole: action.payload.currentUserRole,
          subscriptionStatus: action.payload.subscriptionStatus,
          billingCurrency: action.payload.billingCurrency,
          legacyTrialEndDate: action.payload.legacyTrialEndDate ?? null,
          promoCode: action.payload.promoCode ?? null,
          productAccessStatus: action.payload.productAccessStatus,
          productAccessPauseEndsAt: action.payload.productAccessPauseEndsAt ?? null,
          billingRegularizationBanner: action.payload.billingRegularizationBanner ?? null,
          camLayout: action.payload.camLayout ?? null,
          visualIdentity: null,
          socials: [],
        };
      }
    });
    builder.addCase(fetchWorkspaceById.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error = payload?.message ?? action.error.message ?? 'Failed to fetch workspace';
    });

    builder.addCase(updateWorkspace.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateWorkspace.fulfilled, (state, action) => {
      state.loading = false;
      if (state.currentWorkspace && state.currentWorkspace.id === action.payload.id) {
        state.currentWorkspace.name = action.payload.name;
        state.currentWorkspace.timezone = action.payload.timezone;
        state.currentWorkspace.promoCode = action.payload.promoCode ?? null;
        state.currentWorkspace.camLayout = action.payload.camLayout ?? null;
      }
    });
    builder.addCase(updateWorkspace.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.error = payload?.message ?? action.error.message ?? 'Failed to update workspace';
    });

    builder.addCase(cancelWorkspaceSubscription.pending, (state) => {
      state.subscriptionCancelLoading = true;
      state.subscriptionCancelError = null;
    });
    builder.addCase(cancelWorkspaceSubscription.fulfilled, (state) => {
      state.subscriptionCancelLoading = false;
    });
    builder.addCase(cancelWorkspaceSubscription.rejected, (state, action) => {
      state.subscriptionCancelLoading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.subscriptionCancelError = payload?.message ?? action.error.message ?? null;
    });

    builder.addCase(switchWorkspace, (state, action) => {
      state.currentWorkspace = {
        ...action.payload,
        socials: action.payload.socials ?? [],
      };
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(CURRENT_WORKSPACE_ID_STORAGE_KEY, String(action.payload.id));
        } catch {}
      }
      state.loading = initialState.loading;
      state.error = initialState.error;
      state.createLoading = initialState.createLoading;
      state.socialMediaLoading = initialState.socialMediaLoading;
      state.socialMediaError = initialState.socialMediaError;
      state.team = initialState.team;
      state.teamMeta = initialState.teamMeta;
      state.teamLoading = initialState.teamLoading;
      state.teamError = initialState.teamError;
      state.teamActionLoading = initialState.teamActionLoading;
      state.teamActionError = initialState.teamActionError;
      state.integrations = initialState.integrations;
      state.integrationsLoading = initialState.integrationsLoading;
      state.integrationsError = initialState.integrationsError;
      state.integrationsActionLoading = initialState.integrationsActionLoading;
      state.integrationsActionError = initialState.integrationsActionError;
      state.integrationMetadata = initialState.integrationMetadata;
      state.integrationMetadataLoading = initialState.integrationMetadataLoading;
      state.integrationMetadataError = initialState.integrationMetadataError;
      state.integrationMetadataNotConnected = initialState.integrationMetadataNotConnected;
      state.subscriptionCancelLoading = initialState.subscriptionCancelLoading;
      state.subscriptionCancelError = initialState.subscriptionCancelError;
    });

    builder.addCase(logout.fulfilled, (state) => {
      state.currentWorkspace = null;
      state.workspacesList = null;
      state.workspacesTotalPages = null;
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem(CURRENT_WORKSPACE_ID_STORAGE_KEY);
        } catch {}
      }
      state.error = null;
      state.createLoading = false;
      state.socialMediaLoading = false;
      state.socialMediaError = null;
      state.team = [];
      state.teamMeta = null;
      state.teamLoading = false;
      state.teamError = null;
      state.teamActionLoading = false;
      state.teamActionError = null;
      state.integrations = [];
      state.integrationsLoading = false;
      state.integrationsError = null;
      state.integrationsActionLoading = false;
      state.integrationsActionError = null;
      state.integrationMetadata = [];
      state.integrationMetadataLoading = false;
      state.integrationMetadataError = null;
      state.integrationMetadataNotConnected = false;
      state.subscriptionCancelLoading = false;
      state.subscriptionCancelError = null;
    });
  },
});

export const { setCurrentWorkspace, clearCurrentWorkspace, resetWorkspaceData } = slice.actions;
export default slice.reducer;
export const selectCurrentWorkspace = (state: any) => state.workspace.currentWorkspace;

/**
 * Compte « non payant » : créé sans abonnement (statut NONE). Peut tout explorer
 * mais pas lancer de live. Sert de source de vérité au gating front (bannière, blocage
 * live, limites). À ne pas confondre avec les états bloquants SUSPENDED / TERMINATED
 * (résiliation, pause), gérés séparément.
 */
export const isWorkspaceNonPaying = (workspace: Workspace | null | undefined): boolean =>
  workspace?.subscriptionStatus === 'NONE';

export const selectIsNonPaying = (state: {
  workspace: { currentWorkspace: Workspace | null };
}): boolean => isWorkspaceNonPaying(state.workspace.currentWorkspace);

export const selectHasFreeRegistrationLimit = (state: {
  workspace: { currentWorkspace: Workspace | null };
  billing: { usage: { data: { planName: string } | null } };
}): boolean =>
  hasFreeRegistrationLimit(
    state.workspace.currentWorkspace?.subscriptionStatus,
    state.billing.usage.data?.planName,
  );

/**
 * Compte gratuit pur : non payant (NONE) et sans aucun sursis legacy (`legacyTrialEndDate` nul).
 * Aligné sur le back :
 *  - legacy null  → a accès, ne peut pas lancer de live → bannière + blocage live.
 *  - legacy futur → a accès, peut encore lancer un live (ancien trial valide) → pas de bannière.
 *  - legacy passé → accès entièrement bloqué côté back (bascule paiement) → pas de bannière.
 */
export const isWorkspaceFreeWithoutLiveAccess = (
  workspace: Workspace | null | undefined,
): boolean => isWorkspaceNonPaying(workspace) && workspace?.legacyTrialEndDate == null;

/**
 * Ancien compte trial migré dont le sursis legacy est **expiré** : le back le bascule en 402
 * partout (« cessation de paiement »). Il doit voir le parcours de paiement, pas le traitement
 * gratuit. À distinguer du gratuit pur (`legacyTrialEndDate == null`).
 */
export const isWorkspaceLegacyTrialExpired = (workspace: Workspace | null | undefined): boolean => {
  if (!isWorkspaceNonPaying(workspace)) return false;
  const endDate = workspace?.legacyTrialEndDate;
  return endDate != null && new Date(endDate).getTime() <= Date.now();
};

export const selectIsFreeWithoutLiveAccess = (state: {
  workspace: { currentWorkspace: Workspace | null };
}): boolean => isWorkspaceFreeWithoutLiveAccess(state.workspace.currentWorkspace);
